import PaymentIcon from '@mui/icons-material/Payment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import I18n from 'i18n-js'
import moment from 'moment'
import { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import cloudinary from '@src/lib/Cloudinary'

import { formatWithoutCents } from '../../../../lib/Utils'
import { selectors } from '../../../../stores'
import { availablePaidAdsByDateSelector } from '../../../../stores/paidAds/selectors'
import LegacySvg from '../../LegacySvg'

import LocalAds from './LocalAds'

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      'display': 'flex',
      'padding': theme.spacing(2),
      'cursor': 'pointer',
      '&:hover': {
        background: theme.palette.grey[200],
      },
    },
    paidAdsWalletText: {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  }
})

const PaidAd = ({ paidAd, closeDropdown, name }) => {
  const { classes } = useStyles()
  const history = useHistory()
  const handleClick = useCallback((url) => {
    history.push(url)
    closeDropdown()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Box
      className={classes.root}
      data-testid={`${name}Container`}
      onClick={() => handleClick(paidAd.activateUrl)}
    >
      <Box
        component="img"
        style={{ objectFit: 'cover' }}
        data-testid={`${name}Image`}
        height={90}
        src={cloudinary.instance().url(paidAd.thumbnailSrcId, {
          crop: 'scale',
          width: 'auto',
          fetch_format: 'jpg',
          quality: 'auto',
        })}
        width={90}
      />
      <Box paddingLeft={4} position="relative">
        <Box
          data-testid={`${name}Title`}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="160px"
        >
          <strong>{paidAd.title}</strong>
        </Box>
        <Typography data-testid={`${name}Brand`}>{paidAd.brandName}</Typography>
        <Typography data-testid={`${name}Funds`} style={{ color: 'green' }}>
          <strong>
            {formatWithoutCents.format(paidAd.creditAmount / 100)}
          </strong>
        </Typography>
        <Typography data-testid={`${name}Expires`} variant="body2">
          <strong>{I18n.t('wallet.starts')}</strong>{' '}
          {moment(paidAd.startDate).format('L')}
        </Typography>
        <Box bottom="0px" position="absolute" right="0px">
          <LegacySvg height={'40px'} symbol={'premium-badge'} width={'40px'} />
        </Box>
      </Box>
    </Box>
  )
}

const PaidAdsWallet = ({ name }) => {
  const { classes } = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const paidAds = useSelector(availablePaidAdsByDateSelector)
  const { allocationDetailsTotal } = useSelector(
    selectors.retailerAccountAllocationDetails
      .allocationDetailsTotalForCurrentRetailerAccountSelector,
  )
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const paidAdFunds = useMemo(
    () => paidAds.reduce((acc, current) => acc + current.creditAmount, 0),
    [paidAds],
  )

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <>
      <Button
        cursor="pointer"
        data-testid={`${name}Navbar`}
        onClick={handleClick}
        color="inherit"
        style={{ minWidth: 'auto' }}
        startIcon={
          <PaymentIcon
            data-testid={`${name}NavbarIcon`}
            fontSize="large"
            color="inherit"
          />
        }
      >
        <span className={classes.paidAdsWalletText}>
          {formatWithoutCents.format(
            (paidAdFunds + allocationDetailsTotal) / 100,
          )}
        </span>
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        data-testid={`${name}DropdownContainer`}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box display="flex" justifyContent="center" flexWrap="wrap">
          <Box minWidth={250}>
            <LocalAds
              localAdsTotal={formatWithoutCents.format(
                allocationDetailsTotal / 100,
              )}
              name={name}
            />
          </Box>
          <Box minWidth={250} maxHeight={370} padding={4}>
            <Box
              alignItems="center"
              data-testid={`${name}DropdownTitle`}
              display="flex"
              flexDirection="column"
              marginBottom={2}
            >
              {' '}
              <Box fontSize="h6.fontSize" fontWeight="600" margin={4}>
                {I18n.t('wallet.paidAdTotal')}
                <Box color={'green'} component="span">
                  {formatWithoutCents.format(paidAdFunds / 100)}
                </Box>
              </Box>
            </Box>
            {!paidAds.length && (
              <Box
                data-testid={`${name}NoCamapaignText`}
                fontSize="body2.fontSize"
                fontStyle="italic"
                textAlign="center"
              >
                {I18n.t('wallet.noPremiumAds')}
              </Box>
            )}
            {paidAds.map((paidAd, i) => {
              const key = `paid-ad-${i}`
              return (
                <PaidAd
                  closeDropdown={handleClose}
                  key={key}
                  name={`${name}-${paidAd.id}`}
                  paidAd={paidAd}
                />
              )
            })}
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default PaidAdsWallet
